import React, { useEffect, useState, useContext } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@mui/system';
import {
  TableContainer,
  IconButton,
  TextField,
  TableBody,
  TableCell,
  TableRow,
  Table,
  Button,
  Chip,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';

import {
  fetchSpecializations,
  deleteSpecialization,
  postSpecialization,
  getSpecializations,
  deleteSpecializationService,
} from '../http/doctorAPI';
import { PreparationsGuide } from './PreparationGuides';
import {Context} from "../index";

const Specializations = () => {
  const { features } = useContext(Context);
  const [specializations, setSpecializations] = useState([]);
  const [name, setName] = useState('');
  const [openGuideDialog, setOpenGuideDialog] = useState(false);
  const [selectedSpecializationId, setSelectedSpecializationId] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState(null);
  const [preparationGuide, setPreparationGuide] = useState(null);
  const [expandedSpecializations, setExpandedSpecializations] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchSpecializations().then((data) => {
      const sortedData = data.map((spec) => ({
        ...spec,
        services: spec.services
          ? [...spec.services].sort((a, b) => a.title.localeCompare(b.title, 'ru'))
          : [],
      }));
      setSpecializations(sortedData);
    });
  }, []);

  const addSpecialization = () => {
    postSpecialization({ name }).then((data) => {
      if (!data.error) {
        setSpecializations([data, ...specializations]);
        setName('');
      }
    });
  };

  const removeSpecialization = (id) => {
    deleteSpecialization({ id }).then((data) => {
      if (!data.error) {
        setSpecializations((current) =>
          current.filter((specialization) => specialization.id !== id)
        );
      }
    });
  };

  const removeService = (serviceId, specializationId) => {
    deleteSpecializationService(serviceId).then((data) => {
      if (!data.error) {
        setSpecializations((current) =>
          current.map((spec) => {
            if (spec.id === specializationId) {
              return {
                ...spec,
                services: spec.services?.filter((service) => service.id !== serviceId) || [],
              };
            }
            return spec;
          })
        );
      }
    });
  };

  const handleOpenGuideDialog = (specializationId, serviceId = null) => {
    const specialization = specializations.find((s) => s.id === specializationId);
    
    let guide = null;
    if (serviceId) {
      const service = specialization.services?.find((s) => s.id === serviceId);
      guide = service?.preparationGuide || null;
    } else {
      guide = specialization.preparationGuide || null;
    }
    
    setSelectedSpecializationId(specializationId);
    setSelectedServiceId(serviceId);
    setPreparationGuide(guide);
    setOpenGuideDialog(true);
  };

  const handleGuideCreated = (newGuide) => {
    setSpecializations((current) =>
      current.map((spec) => {
        if (spec.id === selectedSpecializationId) {
          if (selectedServiceId) {
            const updatedServices = spec.services?.map((service) =>
              service.id === selectedServiceId
                ? { ...service, preparationGuide: newGuide }
                : service
            );
            return {
              ...spec,
              services: updatedServices
                ? [...updatedServices].sort((a, b) => a.title.localeCompare(b.title, 'ru'))
                : [],
            };
          } else {
            return { ...spec, preparationGuide: newGuide };
          }
        }
        return spec;
      })
    );
  };

  const handleUpdate = () => {
    setIsLoading(true);
    getSpecializations()
      .then((data) => {
        const sortedData = data.map((spec) => ({
          ...spec,
          services: spec.services
            ? [...spec.services].sort((a, b) => a.title.localeCompare(b.title, 'ru'))
            : [],
        }));
        setSpecializations(sortedData);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseGuideDialog = () => {
    setOpenGuideDialog(false);
    setSelectedSpecializationId(null);
    setSelectedServiceId(null);
    setPreparationGuide(null);
  };

  const handleGuideDeleted = () => {
    setSpecializations((current) =>
      current.map((spec) => {
        if (spec.id === selectedSpecializationId) {
          if (selectedServiceId) {
            const updatedServices = spec.services?.map((service) =>
              service.id === selectedServiceId
                ? { ...service, preparationGuide: null }
                : service
            );
            return {
              ...spec,
              services: updatedServices
                ? [...updatedServices].sort((a, b) => a.title.localeCompare(b.title, 'ru'))
                : [],
            };
          } else {
            return { ...spec, preparationGuide: null };
          }
        }
        return spec;
      })
    );
  };

  const toggleExpanded = (specializationId) => {
    setExpandedSpecializations((prev) => ({
      ...prev,
      [specializationId]: !prev[specializationId],
    }));
  };

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Вы можете сгруппировать врачей по специализациям. Они будут отображаться в боте по кнопке «Врачи». Для каждой специализации можно добавить памятку о подготовке к приему.
      </Typography>
      <div style={{ display: 'flex', alignItems:'center', marginBottom: '15px'}}>
        <Box sx={{display: 'flex', alignItems: 'center', mr: 1,  width: 400}}>
          <TextField
            id='outlined-basic'
            label='Название специализации'
            variant='outlined'
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mr: 1 }}
          />
          <IconButton color='primary' size='large' onClick={addSpecialization}>
            <AddCircleOutlineIcon fontSize='inherit' />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
          <Typography>
            Памятки можно настроить только для специализаций, связанных с МИС.
          </Typography>
          <Typography variant='caption' color='text.secondary'>
           Длительность синхронизации зависит от количества специализаций (примерно {Math.floor(specializations.length * 2.5)} сек.)
          </Typography>
        </Box>
        <Button 
          variant='contained' 
          onClick={handleUpdate} 
          sx={{ ml: 2, height: '50px', width: '400px' }}
          disabled={!features?.features?.syncSpecializations || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isLoading ? 'Загрузка...' : 'Подгрузить МИС специализации'}
        </Button>
      </div> 
      <TableContainer
        elevation={0}
        sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, minWidth: 760, maxHeight: 'calc(75vh - 70px)'}}
      >
        <Table sx={{ minWidth: 700 }} aria-label='simple table'>
          <TableBody>
            {specializations.map((specialization) => (
              <React.Fragment key={specialization.id}>
                <TableRow>
                  <TableCell align='left' sx={{ p: 1, pl: 2 }}>
                    {specialization.name}
                  </TableCell>
                  <TableCell align='left' sx={{ p: 1 }}>
                    <Chip
                      label={specialization.doctorProfessionId ? 'Связано с МИС' : 'Не связано с МИС'}
                      color={specialization.doctorProfessionId ? 'success' : 'error'}
                      variant='outlined'
                      size='small'
                    />
                    {specialization.doctorProfessionId && (
                      <>
                        <Button
                          variant='contained'
                          onClick={() => handleOpenGuideDialog(specialization.id)}
                          sx={{
                            ml: 3,
                            minWidth: '150px',
                            fontSize: '12px',
                            height: '22px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0 8px',
                          }}
                        >
                          {specialization.preparationGuide ? 'Изменить памятку' : 'Создать памятку'}
                        </Button>
                        {specialization.services && specialization.services.length > 0 && (
                          <IconButton
                            onClick={() => toggleExpanded(specialization.id)}
                            sx={{ ml: 1 }}
                            size='small'
                          >
                            <ExpandMoreIcon
                              sx={{
                                transform: expandedSpecializations[specialization.id]
                                  ? 'rotate(180deg)'
                                  : 'rotate(0deg)',
                                transition: 'transform 0.3s',
                              }}
                            />
                          </IconButton>
                        )}
                      </>
                    )}
                  </TableCell>
                  <TableCell align='right' sx={{ p: 1 }}>
                    <IconButton
                      aria-label='delete'
                      onClick={() => removeSpecialization(specialization.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {specialization.services && specialization.services.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ p: 0, border: 0 }}>
                      <Collapse
                        in={expandedSpecializations[specialization.id]}
                        timeout='auto'
                        unmountOnExit
                      >
                        <Box sx={{ bgcolor: 'grey.50', p: 2 }}>
                          <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 'bold' }}>
                            Услуги ({specialization.services.length}):
                          </Typography>
                          <List dense>
                            {specialization.services.map((service) => (
                              <ListItem
                                key={service.id}
                                sx={{
                                  border: 1,
                                  borderColor: 'grey.300',
                                  borderRadius: 1,
                                  mb: 0.5,
                                  bgcolor: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <ListItemText
                                  primary={service.title}
                                  secondary={service.shortDescription || service.code}
                                />
                                <Button
                                  variant='outlined'
                                  size='small'
                                  onClick={() => handleOpenGuideDialog(specialization.id, service.id)}
                                  sx={{ ml: 2 }}
                                >
                                  {service.preparationGuide ? 'Изменить памятку' : 'Создать памятку'}
                                </Button>
                                <IconButton
                                  aria-label='delete'
                                  size='small'
                                  onClick={() => removeService(service.id, specialization.id)}
                                  sx={{ ml: 1 }}
                                >
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PreparationsGuide
        specializationId={selectedSpecializationId}
        specializationServiceId={selectedServiceId}
        open={openGuideDialog}
        onClose={handleCloseGuideDialog}
        onGuideCreated={handleGuideCreated}
        onGuideDeleted={handleGuideDeleted}
        preparationGuide={preparationGuide}
      />
    </Box>
  );
};

export default Specializations;
